import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { serviceFaqs } from "@/data/serviceFaqs";
import { NeighborhoodLinks } from "@/components/NeighborhoodLinks";
import { serviceAreas } from "@/data/serviceAreas";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import img from "@/assets/smart-home.webp";
import smartHomeVideo from "@/assets/smart-home-automation.mp4.asset.json";

const bullets = [
  "Top-tier dealer of leading whole-home automation platforms",
  "Custom user interfaces for tablets, phones, and keypads",
  "Voice control via every major smart-speaker ecosystem",
  "Scene programming for daily routines and entertaining",
  "Remote monitoring and 24/7 system support",
  "Integration with virtually every smart device on the market",
];

export const Route = createFileRoute("/services/smart-home")({
  head: () => ({
    meta: [
      { title: "Smart Home Automation Install | Apex AV Denver" },
      { name: "description", content: "Whole-home automation across Denver — lighting, shades, HVAC, audio, video & security on one interface. Schedule a free in-home consultation." },
      { name: "keywords", content: "smart home automation Denver, Control4 dealer Denver, Control4 installer Denver, Control4 home automation Denver, home automation installer Denver, whole-home automation Denver, smart home installation Denver, lighting automation Denver, shade automation Denver, smart thermostat integration Denver, home automation programming Denver, luxury smart home Denver, residential technology integrator Denver" },
      { property: "og:title", content: "Smart Home Automation — Apex AV Denver" },
      { property: "og:description", content: "One interface for every system in your Denver home. Schedule a free consultation." },
      { property: "og:image", content: abs(img) },
      { name: "twitter:image", content: abs(img) },
      { property: "og:url", content: "https://apexav.com/services/smart-home" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Smart Home Automation — Apex AV Denver" },
      { name: "twitter:description", content: "One interface for every system in your Denver home. Schedule a free consultation." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/smart-home" }, { rel: "preload", as: "image", href: img, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "smart-home",
            name: "Smart Home Automation",
            description: "Unified lighting, climate, audio, and security automation in Denver.",
            pageTitle: "Smart Home Automation Install | Apex AV Denver",
            pageDescription: "Whole-home automation across Denver — lighting, shades, HVAC, audio, video & security on one interface. Schedule a free in-home consultation.",
            image: img,
            faqs: serviceFaqs["smart-home"],
            serviceType: "Smart Home Automation",
            breadcrumbName: "Smart Home Automation",
            offerCatalog: bullets,
          }),
        ),
      },
    ],
  }),
  component: () => (
    <>
    <ServiceDetail
      faqs={serviceFaqs["smart-home"]}
      topics={["smart-home", "av-installation", "control4", "lighting", "shading", "networking", "whole-home-video"]}
      eyebrow="Smart home"
      title="One interface. Every system."
      intro={<>Lighting, shades, climate, audio, video, security, and access — unified into one elegant interface for <strong>smart home automation in Denver</strong>. Designed and programmed by certified <strong>home automation installer</strong> engineers.</>}
      image={img}
      video={smartHomeVideo.url}
      imageAlt="Whole-home smart home automation installation in a Castle Pines Denver luxury residence"
      brands={["sonos", "ecobee", "eero", "luma", "ubiquiti", "wattbox"]}
      bullets={bullets}
      features={[
        { title: "One remote, one app", desc: "Control every system from any room, any device, anywhere." },
        { title: "Programmed for you", desc: "Scenes built around your real schedule — wake, work, dinner, sleep." },
        { title: "Dealer-supported", desc: "We service what we install. Most issues solved remotely in minutes." },
      ]}
      serviceAreas={serviceAreas.map((a) => ({ slug: a.slug, name: a.name }))}
      relatedServices={[
        { to: "/services/control4", label: "Control4 Home Automation", desc: "Authorized Control4 dealer — certified install, programming, and takeover." },
        { to: "/services/home-theater", label: "Custom Home Theater", desc: "Dedicated theater rooms that disappear behind one-button scenes." },
        { to: "/services/lighting", label: "Smart Lighting", desc: "Architectural lighting with circadian scenes and custom keypads." },
        { to: "/services/shading", label: "Motorized Shading", desc: "Whisper-quiet shades that follow the Front Range sun." },
        { to: "/services/climate-control", label: "Smart Climate Control", desc: "Multi-zone HVAC tied to your daily routines." },
        { to: "/services/security", label: "Security & Surveillance", desc: "4K cameras, smart locks, and 24/7 monitored alarms in one app." },
        { to: "/services/networking", label: "Networking & Wi-Fi", desc: "The enterprise backbone every smart home depends on." },
      ]}
    />
    <NeighborhoodLinks
      service="smart-home"
      heading="Smart home installation by neighborhood"
      subheading="Local-first design notes for the communities and HOAs we know inside out."
    />
    </>
  ),
});
